import { getCsrfToken } from './auth';

/**
 * The one place that talks to the Doc Review API (`/api/review/*`).
 *
 * Same contract as `quizApi.js`: CSRF on every write, and FastAPI's `detail` surfaced as the
 * error message, because these endpoints write it as a sentence a person can act on
 * ("A review can start only while the document is in Draft …").
 */
async function call(method, url, body) {
  const opts = { method, headers: {}, credentials: 'include' };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();

  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }
  if (!res.ok) {
    const detail = data?.detail;
    const isObject = detail && typeof detail === 'object';
    const err = new Error((isObject ? detail.message : detail) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const q = (params) => {
  const s = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') s.set(k, v);
  });
  const str = s.toString();
  return str ? `?${str}` : '';
};

export const reviewApi = {
  // panels
  setup: () => call('GET', '/api/review/setup'),
  filters: (projectId, typeLabel) =>
    call('GET', `/api/review/filters${q({ project_id: projectId, type_label: typeLabel })}`),
  checkFilter: (body) => call('POST', '/api/review/check-filter', body),
  panels: () => call('GET', '/api/review/panels'),
  createPanel: (body) => call('POST', '/api/review/panels', body),
  updatePanel: (id, body) => call('PUT', `/api/review/panels/${id}`, body),
  panelConfig: (id) => call('GET', `/api/review/panels/${id}/config`),
  deletePanel: (id) => call('DELETE', `/api/config/${id}`),
  rows: (id, params) => call('GET', `/api/review/panels/${id}/rows${q(params)}`),

  // people & home
  users: () => call('GET', '/api/review/users'),
  home: () => call('GET', '/api/review/home'),

  // reviews
  start: (body) => call('POST', '/api/review/reviews', body),
  findItem: (key) => call('GET', `/api/review/find-item${q({ key })}`),
  itemReviews: (itemId, setupId) =>
    call('GET', `/api/review/items/${encodeURIComponent(itemId)}/reviews${q({ setup_id: setupId })}`),
  review: (id) => call('GET', `/api/review/reviews/${id}`),
  updateReview: (id, body) => call('PATCH', `/api/review/reviews/${id}`, body),
  addReviewers: (id, reviewers) => call('POST', `/api/review/reviews/${id}/reviewers`, { reviewers }),
  removeReviewer: (id, userId) =>
    call('DELETE', `/api/review/reviews/${id}/reviewers/${encodeURIComponent(userId)}`),
  done: (id, done = true) => call('POST', `/api/review/reviews/${id}/done`, { done }),
  opened: (id) => call('POST', `/api/review/reviews/${id}/opened`),
  closeRound: (id) => call('POST', `/api/review/reviews/${id}/close-round`),
  newRound: (id, dueDate) =>
    call('POST', `/api/review/reviews/${id}/new-round`, dueDate ? { due_date: dueDate } : {}),
  setStatus: (id, status) => call('POST', `/api/review/reviews/${id}/status`, { status }),

  // the document & comments (Phase 2)
  document: (id, round) => call('GET', `/api/review/reviews/${id}/document${q({ round })}`),
  fileUrl: (id, round, download, comments) =>
    `/api/review/reviews/${id}/file${q({ round, download: download ? 1 : '', comments: comments ? 1 : '' })}`,
  // The file is FETCHED, never linked: a plain <a href>/iframe to /api/* is a page navigation,
  // and the dev proxy answers a navigation with index.html — the app home, not the file.
  // `comments` writes the review's comments into a Word file as Word comments.
  fileBlob: async (id, round, comments = false) => {
    // no-store: a copy cached before the last comment (or before a restart) has none of them.
    const res = await fetch(reviewApi.fileUrl(id, round, true, comments),
      { credentials: 'include', cache: comments ? 'no-store' : 'default' });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json())?.detail; } catch (_) { /* not JSON */ }
      throw new Error((typeof detail === 'string' && detail) || `Download failed (${res.status})`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    let name = 'document';
    if (m && m[1]) { try { name = decodeURIComponent(m[1]); } catch (_) { name = m[1]; } }
    return { blob: await res.blob(), name };
  },
  // The review record PDF (v4.10.0) — fetched for the same reason as the file.
  recordBlob: async (id) => {
    const res = await fetch(`/api/review/reviews/${id}/record.pdf`, { credentials: 'include' });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json())?.detail; } catch (_) { /* not JSON */ }
      throw new Error((typeof detail === 'string' && detail) || `Download failed (${res.status})`);
    }
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(res.headers.get('Content-Disposition') || '');
    let name = 'Review record.pdf';
    if (m && m[1]) { try { name = decodeURIComponent(m[1]); } catch (_) { name = m[1]; } }
    return { blob: await res.blob(), name };
  },
  // Compare with an earlier revision, on the PDFs (v4.11.0) — the Orcanos QMS "track changes".
  revisions: (id, round) => call('GET', `/api/review/reviews/${id}/revisions${q({ round })}`),
  compare: (id, against, round) => call('GET', `/api/review/reviews/${id}/compare${q({ against, round })}`),
  compareSummary: (id, against, round, force = false) =>
    call('POST', `/api/review/reviews/${id}/compare/summary`, { against, round, force }),
  // The redline PDF — side 'new' (track changes) or 'old' (removals struck through).
  comparePdfBlob: async (id, against, side, round) => {
    const res = await fetch(`/api/review/reviews/${id}/compare.pdf${q({ against, side, round })}`, { credentials: 'include' });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json())?.detail; } catch (_) { /* not JSON */ }
      throw new Error((typeof detail === 'string' && detail) || `Couldn't open the comparison (${res.status})`);
    }
    return res.blob();
  },
  // The draft as PDF (Orcanos' own rendition) — fetched, never linked (Note #85).
  pdfBlob: async (id, round, attachment) => {
    const res = await fetch(`/api/review/reviews/${id}/pdf${q({ round, attachment })}`, { credentials: 'include' });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json())?.detail; } catch (_) { /* not JSON */ }
      throw new Error((typeof detail === 'string' && detail) || `Couldn't open the PDF (${res.status})`);
    }
    return res.blob();
  },
  itemForm: (id) => call('GET', `/api/review/reviews/${id}/item-form`),
  comments: (id, round) => call('GET', `/api/review/reviews/${id}/comments${q({ round })}`),
  addComment: (id, body) => call('POST', `/api/review/reviews/${id}/comments`, body),
  patchComment: (cid, body) => call('PATCH', `/api/review/comments/${cid}`, body),
  deleteComment: (cid) => call('DELETE', `/api/review/comments/${cid}`),

  // notifications
  notifications: () => call('GET', '/api/review/notifications'),
  markRead: (ids) => call('POST', '/api/review/notifications/read', { ids: ids || [] }),
};

/** Hand a fetched blob to the browser as a download. */
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "DOCX" / "XLSX" / "PDF" / the extension, for the file-type chip. */
export function fileTag(fileName) {
  const m = /\.([a-z0-9]+)$/i.exec(fileName || '');
  return m ? m[1].toUpperCase() : 'FILE';
}

/** How a file kind is commented on — the one sentence the list and Start review both show. */
export const COMMENT_MODE = {
  docx: 'Comments in the text',
  pdf: 'Comments in the text',
  xlsx: 'Comments on cells',
  other: 'Whole-document comments',
};
